import {
  ADMIN_ACCESS_TOKEN_KEY,
  ADMIN_REFRESH_TOKEN_KEY,
} from './admin-session-keys';
import {
  clearRefreshEndedSession,
  didRefreshEndSession,
  markSessionEndedByRefresh,
  performTokenRefresh,
  setAccessTokenResolver,
  setActionOnTokenRefresh,
  setSdkTokens,
} from './sdk-client';
import { storageService } from './storage.service';

export const ADMIN_ROLE = 'admin' as const;
export const SUPERADMIN_ROLE = 'superadmin' as const;

export type AdminSessionTokens = {
  accessToken: string;
  refreshToken: string;
};

export function hasAdminRole(roles: readonly string[]): boolean {
  return roles.includes(ADMIN_ROLE) || roles.includes(SUPERADMIN_ROLE);
}

export function isSuperAdminRole(roles: readonly string[]): boolean {
  return roles.includes(SUPERADMIN_ROLE);
}

/** Reads permission claims from an admin access token. Signature is not verified. */
export function readAccessTokenPermissions(accessToken: string): string[] {
  const segment = accessToken.split('.')[1];
  if (!segment) return [];

  try {
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );
    const payload = JSON.parse(atob(padded)) as { permissions?: unknown };
    if (!Array.isArray(payload.permissions)) return [];
    return payload.permissions.filter(
      (permission): permission is string => typeof permission === 'string',
    );
  } catch {
    return [];
  }
}

const adminSessionListeners = new Set<() => void>();

/** Notifies subscribers when the stored admin access token changes. */
export function subscribeAdminSession(listener: () => void): () => void {
  adminSessionListeners.add(listener);
  return () => {
    adminSessionListeners.delete(listener);
  };
}

function notifyAdminSessionChanged(): void {
  for (const listener of adminSessionListeners) {
    listener();
  }
}

export function saveAdminSession(tokens: AdminSessionTokens): void {
  storageService.setSession(ADMIN_ACCESS_TOKEN_KEY, tokens.accessToken);
  storageService.setSession(ADMIN_REFRESH_TOKEN_KEY, tokens.refreshToken);
  setSdkTokens(tokens.accessToken, tokens.refreshToken);
  notifyAdminSessionChanged();
}

export function loadAdminSession(): AdminSessionTokens | null {
  const accessToken = storageService.getSession(ADMIN_ACCESS_TOKEN_KEY);
  const refreshToken = storageService.getSession(ADMIN_REFRESH_TOKEN_KEY);

  if (!accessToken?.trim() || !refreshToken?.trim()) {
    return null;
  }

  return {
    accessToken: accessToken.trim(),
    refreshToken: refreshToken.trim(),
  };
}

export function clearAdminSession(): void {
  storageService.removeSession(ADMIN_ACCESS_TOKEN_KEY);
  storageService.removeSession(ADMIN_REFRESH_TOKEN_KEY);
  setSdkTokens(null, null);
  notifyAdminSessionChanged();
}

export type AdminUnauthorizedRecovery = 'recovered' | 'ended' | 'unavailable';

/**
 * Decides whether a 401 can be healed with the stored refresh token.
 * A terminal refresh (expired, superseded, or blocked) ends the admin session.
 * A transport or server failure leaves the stored session untouched.
 */
export async function recoverAdminSessionAfterUnauthorized(): Promise<AdminUnauthorizedRecovery> {
  if (didRefreshEndSession() || loadAdminSession() == null) {
    return 'ended';
  }

  const session = loadAdminSession();
  if (!session) return 'ended';

  const outcome = await performTokenRefresh(session.refreshToken);
  if (outcome.status === 'valid') {
    clearRefreshEndedSession();
    saveAdminSession({
      accessToken: outcome.accessToken,
      refreshToken: outcome.refreshToken,
    });
    return 'recovered';
  }

  if (outcome.status === 'error') {
    return 'unavailable';
  }

  markSessionEndedByRefresh();
  return 'ended';
}

/**
 * Wire SDK Bearer auth to the admin sessionStorage tokens.
 * Safe to call multiple times (e.g. on provider mount).
 */
export function bootstrapAdminSdkAuth(): void {
  clearRefreshEndedSession();
  setAccessTokenResolver(() => loadAdminSession()?.accessToken ?? null);
  setActionOnTokenRefresh((accessToken, refreshToken) => {
    saveAdminSession({ accessToken, refreshToken });
  });

  const session = loadAdminSession();
  if (session) {
    setSdkTokens(session.accessToken, session.refreshToken);
  }
}
